import React, {
  Fragment,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { Box, Button, Collapse } from "@mui/material";
import { AppModal } from "global/components/AppModal";
import { ModalTone } from "global/components/AppModal/AppModal.tokens";
import strings from "global/constants/StringConstants";
import { fillCopy } from "helpers/methods";
import styles from "./PresendReview.styles";
import tokens, {
  ReviewSeverityTone,
  ReviewStatusTone,
} from "./PresendReview.tokens";
import {
  ReviewCheckIcon,
  ReviewChevronIcon,
  ReviewClockIcon,
  ReviewDismissIcon,
  ReviewDocumentIcon,
  ReviewInfoIcon,
  ReviewJumpIcon,
  ReviewLockIcon,
  ReviewNotCheckedIcon,
  ReviewReadyIcon,
  ReviewSpinnerIcon,
  ReviewUnavailableIcon,
  ReviewWarningIcon,
} from "./PresendReviewIcons";
import {
  countChips,
  dismissalKey,
  documentDisplayName,
  documentMeta,
  groupIssues,
  isInfo,
  isIssue,
  isNotChecked,
  severityLabel,
  statusLabel,
} from "./PresendReviewHelper";
import {
  PresendFinding,
  PresendReviewDocument,
  PresendReviewFocus,
  PresendReviewResponse,
  PresendReviewState,
} from "./PresendReviewTypes";

const copy = strings.presendReview;

/** The modal system's header glyph size. */
const HEADER_ICON_SIZE = 19;

const statusTone = (status: string): ReviewStatusTone =>
  status === "ready" || status === "needs_attention" ? status : "not_reviewed";

const severityTone = (severity: string): ReviewSeverityTone =>
  severity === "high" || severity === "medium" ? severity : "low";

/** The header tile per review status: the modal system's own tones, with the design's glyphs. */
const STATUS_HEADER: Record<ReviewStatusTone, { tone: ModalTone; icon: ReactNode }> = {
  ready: { tone: "success", icon: <ReviewReadyIcon size={HEADER_ICON_SIZE} /> },
  needs_attention: {
    tone: "amber",
    icon: <ReviewWarningIcon size={HEADER_ICON_SIZE} />,
  },
  not_reviewed: {
    tone: "neutral",
    icon: <ReviewNotCheckedIcon size={HEADER_ICON_SIZE} />,
  },
};

const STATUS_PILL_ICON: Record<ReviewStatusTone, ReactNode> = {
  ready: <ReviewCheckIcon size={tokens.statusPill.iconSize} />,
  needs_attention: <ReviewWarningIcon size={tokens.statusPill.iconSize} />,
  not_reviewed: <ReviewNotCheckedIcon size={tokens.statusPill.iconSize} />,
};

/** Seconds left on a 429's countdown, re-read every second while it runs. */
const useSecondsUntil = (deadline: number | null) => {
  const secondsLeft = () =>
    deadline === null
      ? 0
      : Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
  const [seconds, setSeconds] = useState(secondsLeft);
  useEffect(() => {
    setSeconds(secondsLeft());
    if (deadline === null) return;
    const timer = window.setInterval(() => {
      const left = secondsLeft();
      setSeconds(left);
      if (left <= 0) window.clearInterval(timer);
    }, 1000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadline]);
  return seconds;
};

/** The nearest ancestor that scrolls -- the dialog body. */
const scrollParentOf = (element: HTMLElement): HTMLElement | null => {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
  }
  return null;
};

/**
 * Whether a sticky document header is pinned at the top of its scroll area. It pins 1px past the scroll
 * area's edge (`disclosure.stickyTop`), so pinned is the one place its top edge is clipped. Observed
 * against the dialog body itself, so a viewport that cuts off the top of the popup (a short landscape
 * phone) does not read as pinned. Only watched while `active` (the document is open).
 */
const usePinned = (ref: RefObject<HTMLElement>, active: boolean) => {
  const [pinned, setPinned] = useState(false);
  useEffect(() => {
    const element = ref.current;
    const root = element && scrollParentOf(element);
    if (!active || !element || !root || typeof IntersectionObserver === "undefined") {
      setPinned(false);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) =>
        setPinned(
          entry.intersectionRatio < 1 &&
            entry.boundingClientRect.top < entry.intersectionRect.top,
        ),
      { root, threshold: [1] },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, active]);
  return pinned;
};

/** "PRE-SEND REVIEW" stacked over the title, inside the dialog's heading. */
const HeaderTitle = ({ title }: { title: string }) => (
  <>
    <Box component="span" sx={styles.eyebrow}>
      {copy.eyebrow}
    </Box>
    {/* Keeps the dialog's accessible name two words apart ("Pre-send review 2 things…"). */}
    {" "}
    {title}
  </>
);

interface DocumentHeaderProps {
  fileName: string;
  meta?: string;
  trailing: ReactNode;
  /** Makes the header the button that opens and closes the document's body (several documents). */
  toggle?: { expanded: boolean; controls: string; onToggle: () => void };
  /** No toggle, but among headers that have one: an empty chevron slot keeps the pills in line. */
  alignWithToggles?: boolean;
}

const DocumentHeader = ({
  fileName,
  meta,
  trailing,
  toggle,
  alignWithToggles = false,
}: DocumentHeaderProps) => {
  const toggleRef = useRef<HTMLButtonElement>(null);
  const pinned = usePinned(toggleRef, Boolean(toggle?.expanded));
  const handleToggle = () => {
    if (!toggle) return;
    const header = toggleRef.current;
    const scroller = header && scrollParentOf(header);
    const section = header?.parentElement;
    if (toggle.expanded && pinned && scroller && section) {
      // Closed from its pinned spot, part-way down its findings: first bring the document back to where it
      // starts (the header does not move on screen), so closing it leaves the sender at this document
      // instead of somewhere further down the list.
      scroller.scrollTop +=
        section.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top -
        tokens.disclosure.bleedY;
    }
    toggle.onToggle();
  };
  // Spans throughout: the same content sits inside a <button> when the header opens and closes.
  const content = (
    <>
      <Box component="span" sx={styles.documentTile}>
        <ReviewDocumentIcon size={tokens.document.iconSize} />
      </Box>
      <Box component="span" sx={styles.documentText}>
        <Box component="span" sx={styles.documentName}>
          {documentDisplayName(fileName)}
        </Box>
        {meta && (
          <Box component="span" sx={styles.documentMeta}>
            {meta}
          </Box>
        )}
      </Box>
      <Box component="span" sx={styles.documentTrailing}>
        {trailing}
      </Box>
      {toggle && (
        <Box component="span" sx={styles.chevron(toggle.expanded)}>
          <ReviewChevronIcon size={tokens.disclosure.chevronSize} />
        </Box>
      )}
      {!toggle && alignWithToggles && (
        <Box component="span" sx={styles.chevronSpacer} />
      )}
    </>
  );
  if (!toggle) {
    return (
      <Box
        sx={
          alignWithToggles ? styles.documentHeaderAligned : styles.documentHeader
        }
      >
        {content}
      </Box>
    );
  }
  return (
    <Box
      ref={toggleRef}
      component="button"
      type="button"
      sx={styles.documentToggle(pinned)}
      aria-expanded={toggle.expanded}
      aria-controls={toggle.controls}
      onClick={handleToggle}
    >
      {content}
    </Box>
  );
};

const SkeletonCard = () => (
  <Box sx={styles.skeletonCard} aria-hidden="true">
    <Box sx={styles.skeletonBar(tokens.skeleton.title)} />
    <Box sx={styles.skeletonBar(tokens.skeleton.lineLong)} />
    <Box sx={styles.skeletonBar(tokens.skeleton.lineShort)} />
  </Box>
);

interface FindingCardProps {
  finding: PresendFinding;
  fileName: string;
  /** Takes the finding's `dismissalKey`. */
  onDismiss: (key: string) => void;
  /** Absent where there is no editor to show the page in (the phone layout): the link is hidden. */
  onShowOnPage?: (focus: PresendReviewFocus) => void;
}

const FindingCard = ({
  finding,
  fileName,
  onDismiss,
  onShowOnPage,
}: FindingCardProps) => {
  const tone = severityTone(finding.severity);
  const page = typeof finding.page === "number" && finding.page > 0 ? finding.page : null;
  return (
    <Box component="li" sx={styles.card(tone)}>
      <Box sx={styles.cardTop}>
        <Box component="span" sx={styles.severityChip(tone)}>
          {severityLabel(finding.severity)}
        </Box>
        <Box
          component="button"
          type="button"
          sx={styles.dismiss}
          onClick={() => onDismiss(dismissalKey(fileName, finding.id))}
          aria-label={`${copy.dismiss}: ${finding.title}`}
        >
          <ReviewDismissIcon size={tokens.card.dismissIconSize} />
          {copy.dismiss}
        </Box>
      </Box>
      <Box component="p" sx={styles.cardTitle}>
        {finding.title}
      </Box>
      {finding.detail && (
        <Box component="p" sx={styles.cardDetail}>
          {finding.detail}
        </Box>
      )}
      {finding.snippet && (
        <Box component="p" sx={styles.snippet}>
          {finding.snippet}
        </Box>
      )}
      {finding.suggestion && (
        <Box sx={styles.whatToDo}>
          <Box component="p" sx={styles.whatToDoLabel}>
            {copy.whatToDo}
          </Box>
          <Box component="p" sx={styles.suggestion}>
            {finding.suggestion}
          </Box>
        </Box>
      )}
      {page !== null && onShowOnPage && (
        <Box
          component="button"
          type="button"
          sx={styles.showOnPage}
          onClick={() =>
            onShowOnPage({ fileName, page, fieldId: finding.fieldId })
          }
        >
          <ReviewJumpIcon size={tokens.card.locationIconSize} />
          {fillCopy(copy.showOnPage, { page })}
        </Box>
      )}
    </Box>
  );
};

const NotCheckedRow = ({ finding }: { finding: PresendFinding }) => (
  <Box component="li" sx={styles.notCheckedRow}>
    <Box sx={styles.notCheckedIcon}>
      <ReviewNotCheckedIcon size={tokens.notice.iconSize} />
    </Box>
    <Box sx={styles.noticeText}>
      <Box component="p" sx={styles.noticeTitle}>
        {finding.title}
      </Box>
      {finding.detail && (
        <Box component="p" sx={styles.noticeDetail}>
          {finding.detail}
        </Box>
      )}
    </Box>
  </Box>
);

interface DocumentReviewProps {
  document: PresendReviewDocument;
  dismissed: string[];
  /** Takes the finding's `dismissalKey`. */
  onDismiss: (key: string) => void;
  onShowOnPage?: (focus: PresendReviewFocus) => void;
  /** Set when the review covers several documents: the header opens and closes the body. */
  collapse?: { id: string; expanded: boolean; onToggle: () => void };
}

/** One document: its header, its issues by category, what could not be checked, then its notes. */
const DocumentReview = ({
  document,
  dismissed,
  onDismiss,
  onShowOnPage,
  collapse,
}: DocumentReviewProps) => {
  const tone = statusTone(document.status);
  const visible = document.findings.filter(
    (f) => !dismissed.includes(dismissalKey(document.fileName, f.id)),
  );
  const groups = groupIssues(visible);
  const notChecked = visible.filter(isNotChecked);
  const notes = visible.filter(isInfo);
  const hasBody = groups.length > 0 || notChecked.length > 0 || notes.length > 0;
  // Nothing left to open (no findings, or all dismissed): the header stays a plain header.
  const toggle = collapse && hasBody ? collapse : undefined;
  const body = hasBody && (
    <Box sx={styles.documentBody}>
      {groups.map((group) => (
        <Fragment key={group.category}>
          <Box component="h3" sx={styles.categoryHeading}>
            {group.heading}
          </Box>
          <Box component="ul" sx={styles.list}>
            {group.findings.map((finding) => (
              <FindingCard
                key={finding.id}
                finding={finding}
                fileName={document.fileName}
                onDismiss={onDismiss}
                onShowOnPage={onShowOnPage}
              />
            ))}
          </Box>
        </Fragment>
      ))}
      {notChecked.length > 0 && (
        <>
          <Box component="h3" sx={styles.categoryHeading}>
            {copy.couldNotCheckHeading}
          </Box>
          <Box component="ul" sx={styles.list}>
            {notChecked.map((finding) => (
              <NotCheckedRow key={finding.id} finding={finding} />
            ))}
          </Box>
        </>
      )}
      {notes.map((finding) => (
        <Box key={finding.id} sx={styles.infoRow}>
          <Box sx={styles.infoIcon}>
            <ReviewInfoIcon size={tokens.notice.infoIconSize} />
          </Box>
          {finding.title}
        </Box>
      ))}
    </Box>
  );
  return (
    <Box sx={styles.document}>
      <DocumentHeader
        fileName={document.fileName}
        meta={documentMeta(document)}
        trailing={
          <Box component="span" sx={styles.statusPill(tone)}>
            {STATUS_PILL_ICON[tone]}
            {statusLabel(document.status)}
          </Box>
        }
        toggle={
          toggle && {
            expanded: toggle.expanded,
            controls: toggle.id,
            onToggle: toggle.onToggle,
          }
        }
        alignWithToggles={Boolean(collapse)}
      />
      {toggle ? (
        <Collapse in={toggle.expanded} id={toggle.id}>
          {body}
        </Collapse>
      ) : (
        body
      )}
    </Box>
  );
};

interface ReviewDocumentsProps {
  review: PresendReviewResponse;
  dismissed: string[];
  /** Takes the finding's `dismissalKey`. */
  onDismiss: (key: string) => void;
  onShowOnPage?: (focus: PresendReviewFocus) => void;
}

/**
 * The result's documents. One document is shown open; with several, each header opens and closes its
 * findings so the popup is not one long scroll. The first document with an issue starts open, the rest
 * closed (their header still shows the status and the counts). Lives inside the dialog, so every opening
 * of the popup starts from that default again.
 */
const ReviewDocuments = ({
  review,
  dismissed,
  onDismiss,
  onShowOnPage,
}: ReviewDocumentsProps) => {
  const collapsible = review.documents.length > 1;
  const [expanded, setExpanded] = useState<number[]>(() => {
    const first = review.documents.findIndex((d) =>
      d.findings.some(
        (f) => isIssue(f) && !dismissed.includes(dismissalKey(d.fileName, f.id)),
      ),
    );
    return first >= 0 ? [first] : [];
  });
  const toggle = (index: number) =>
    setExpanded((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  return (
    <Documents>
      {review.documents.map((document, index) => (
        <DocumentReview
          key={`${document.fileName}-${index}`}
          document={document}
          dismissed={dismissed}
          onDismiss={onDismiss}
          onShowOnPage={onShowOnPage}
          collapse={
            collapsible
              ? {
                  id: `presend-review-document-${index}`,
                  expanded: expanded.includes(index),
                  onToggle: () => toggle(index),
                }
              : undefined
          }
        />
      ))}
    </Documents>
  );
};

/** The summary under the title, and the count chips under that. */
const ResultSubtitle = ({ review }: { review: PresendReviewResponse }) => {
  const chips = countChips(review.counts);
  return (
    <>
      {review.summary?.message}
      {chips.length > 0 && (
        <Box component="span" sx={styles.countRow}>
          {chips.map((chip) => (
            <Box component="span" key={chip.key} sx={styles.countChip}>
              {chip.key === "notChecked" ? (
                <Box component="span" sx={styles.countIcon}>
                  <ReviewNotCheckedIcon size={tokens.countChip.iconSize} />
                </Box>
              ) : (
                <Box
                  component="span"
                  sx={styles.dot(chip.key as ReviewSeverityTone)}
                />
              )}
              {chip.label}
            </Box>
          ))}
        </Box>
      )}
    </>
  );
};

/** Documents in request order, a hairline between each. */
const Documents = ({ children }: { children: ReactNode[] }) => (
  <Box sx={styles.documents}>
    {children.map((child, index) => (
      <Fragment key={index}>
        {index > 0 && <Box sx={styles.documentDivider} />}
        {child}
      </Fragment>
    ))}
  </Box>
);

export interface PresendReviewModalProps {
  open: boolean;
  state: PresendReviewState;
  /** The documents under review, for the checking rows. */
  fileNames: string[];
  dismissed: string[];
  /** Takes the finding's `dismissalKey` (document + finding). */
  onDismiss: (key: string) => void;
  /** Brings back the dismissals of the review on screen (their `dismissalKey`s). */
  onRestoreDismissed: (keys: string[]) => void;
  onClose: () => void;
  onBackToEditor: () => void;
  /** Omit where there is no editor to show a page in (the phone layout): the page links are hidden. */
  onShowOnPage?: (focus: PresendReviewFocus) => void;
  /** On to the next step -- the step change the review interrupted. */
  onContinue: () => void;
  onRetry: () => void;
  onSignIn: () => void;
}

/**
 * The pre-send review popup (Figma "Pre-send review popup"). Opens over the editor on Next and never
 * blocks it: every state -- checking, a result, or a review that could not run -- keeps a Continue button.
 */
const PresendReviewModal = ({
  open,
  state,
  fileNames,
  dismissed,
  onDismiss,
  onRestoreDismissed,
  onClose,
  onBackToEditor,
  onShowOnPage,
  onContinue,
  onRetry,
  onSignIn,
}: PresendReviewModalProps) => {
  const retrySeconds = useSecondsUntil(
    state.phase === "rateLimited" ? state.retryAt : null,
  );

  const backButton = (
    <Button variant="outlined" onClick={onBackToEditor}>
      {copy.backToEditor}
    </Button>
  );
  const continueButton = (label: string) => (
    <Button variant="contained" onClick={onContinue}>
      {label}
    </Button>
  );
  const tryAgainButton = (
    <Button variant="outlined" onClick={onRetry} sx={styles.tryAgainButton}>
      {copy.tryAgain}
    </Button>
  );

  const shared = { open, onClose, width: tokens.modal.width };
  /** The states with nothing to list: the header's summary is the whole message. */
  const bodyless = {
    ...shared,
    disableBodyPadding: true,
    headerSx: { pb: "20px" },
  };

  if (state.phase === "loading") {
    return (
      <AppModal
        {...shared}
        tone="default"
        icon={
          <Box component="span" sx={styles.spinner}>
            <ReviewSpinnerIcon size={HEADER_ICON_SIZE} />
          </Box>
        }
        title={
          <HeaderTitle
            title={fileNames.length > 1 ? copy.loadingTitlePlural : copy.loadingTitle}
          />
        }
        subtitle={copy.loadingMessage}
        footerStart={
          <Box component="p" sx={styles.footerNote}>
            {copy.loadingNote}
          </Box>
        }
        footer={
          <>
            {backButton}
            {continueButton(copy.continueAnyway)}
          </>
        }
      >
        <Box aria-busy="true">
          <Documents>
            {fileNames.map((fileName, index) => (
              <Box key={`${fileName}-${index}`} sx={styles.document}>
                <DocumentHeader
                  fileName={fileName}
                  trailing={
                    <Box component="span" sx={styles.checking}>
                      {copy.checking}
                    </Box>
                  }
                />
                {index === 0 && (
                  <Box sx={styles.documentBody}>
                    <SkeletonCard />
                    <SkeletonCard />
                  </Box>
                )}
              </Box>
            ))}
          </Documents>
        </Box>
      </AppModal>
    );
  }

  if (state.phase === "result") {
    const { review } = state;
    const header = STATUS_HEADER[statusTone(review.status)];
    const findingKeys = review.documents.flatMap((d) =>
      d.findings.map((f) => dismissalKey(d.fileName, f.id)),
    );
    const dismissedHere = dismissed.filter((key) => findingKeys.includes(key));
    const hasIssues = review.documents.some((d) => d.findings.some(isIssue));
    return (
      <AppModal
        {...shared}
        tone={header.tone}
        icon={header.icon}
        title={<HeaderTitle title={review.summary?.title ?? ""} />}
        subtitle={<ResultSubtitle review={review} />}
        footerStart={
          dismissedHere.length > 0 ? (
            <Box component="p" sx={styles.footerNote}>
              {fillCopy(copy.dismissedCount, { count: dismissedHere.length })}
              {" · "}
              <Box
                component="button"
                type="button"
                sx={styles.footerLink}
                onClick={() => onRestoreDismissed(dismissedHere)}
              >
                {copy.showDismissed}
              </Box>
            </Box>
          ) : undefined
        }
        footer={
          <>
            {backButton}
            {continueButton(hasIssues ? copy.continueAnyway : copy.continue)}
          </>
        }
      >
        <ReviewDocuments
          review={review}
          dismissed={dismissed}
          onDismiss={onDismiss}
          onShowOnPage={onShowOnPage}
        />
      </AppModal>
    );
  }

  if (state.phase === "rateLimited") {
    return (
      <AppModal
        {...bodyless}
        tone="amber"
        icon={<ReviewClockIcon size={HEADER_ICON_SIZE} />}
        title={<HeaderTitle title={copy.rateLimitedTitle} />}
        subtitle={fillCopy(copy.rateLimitedMessage, {
          seconds: Math.max(retrySeconds, 1),
        })}
        footer={
          <>
            {retrySeconds > 0 ? (
              <Button variant="outlined" disabled>
                {fillCopy(copy.tryAgainIn, { seconds: retrySeconds })}
              </Button>
            ) : (
              tryAgainButton
            )}
            {continueButton(copy.continue)}
          </>
        }
      />
    );
  }

  if (state.phase === "session") {
    return (
      <AppModal
        {...bodyless}
        tone="neutral"
        icon={<ReviewLockIcon size={HEADER_ICON_SIZE} />}
        title={<HeaderTitle title={copy.sessionTitle} />}
        subtitle={copy.sessionMessage}
        footer={
          <>
            <Button variant="outlined" onClick={onClose}>
              {copy.cancel}
            </Button>
            <Button variant="contained" onClick={onSignIn}>
              {copy.signIn}
            </Button>
          </>
        }
      />
    );
  }

  return (
    <AppModal
      {...bodyless}
      tone="neutral"
      icon={<ReviewUnavailableIcon size={HEADER_ICON_SIZE} />}
      title={<HeaderTitle title={copy.unavailableTitle} />}
      subtitle={copy.unavailableMessage}
      footer={
        <>
          {tryAgainButton}
          {continueButton(copy.continue)}
        </>
      }
    />
  );
};

export default PresendReviewModal;
